import { useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, ButtonGroup, IconButton, Menu, MenuItem, ListItemIcon, Tooltip, Typography } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PercentIcon from "@mui/icons-material/Percent";
import TuneIcon from "@mui/icons-material/Tune";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BusinessIcon from "@mui/icons-material/Business";

const ActionGroup = ({ label, children }) => (
  <Box role="group" aria-label={label} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
      {label}
    </Typography>
    <ButtonGroup variant="outlined" color="primary" size="small">
      {children}
    </ButtonGroup>
  </Box>
);

ActionGroup.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

// Header actions of the Adyen settings card, grouped by what they touch: money moving out of the
// merchant's balance, and the commission Adyen takes on terminal payments. Rare one-off admin tasks
// sit in the overflow menu.
const AdyenSettingsActions = ({
  busy,
  onTransferMoney,
  onPayoutSchedule,
  onBankAccount,
  onEditRates,
  onViewConfig,
  onUpdateBusinessLines,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleUpdateBusinessLines = () => {
    setMenuAnchor(null);
    onUpdateBusinessLines();
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: { xs: 2, md: 3 } }}>
      <ActionGroup label="Payouts">
        <Button startIcon={<SwapHorizIcon />} onClick={onTransferMoney} disabled={busy}>
          Transfer money
        </Button>
        <Button startIcon={<ScheduleIcon />} onClick={onPayoutSchedule} disabled={busy}>
          Payout schedule
        </Button>
        <Button startIcon={<AccountBalanceIcon />} onClick={onBankAccount} disabled={busy}>
          Bank account
        </Button>
      </ActionGroup>

      <ActionGroup label="Commission">
        <Button startIcon={<PercentIcon />} onClick={onEditRates} disabled={busy}>
          Edit rates
        </Button>
        <Button startIcon={<TuneIcon />} onClick={onViewConfig}>
          Adyen config
        </Button>
      </ActionGroup>

      <Tooltip title="More Adyen actions">
        <IconButton
          aria-label="More Adyen actions"
          aria-haspopup="true"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleUpdateBusinessLines} disabled={busy}>
          <ListItemIcon>
            <BusinessIcon fontSize="small" />
          </ListItemIcon>
          Update business lines
        </MenuItem>
      </Menu>
    </Box>
  );
};

AdyenSettingsActions.propTypes = {
  busy: PropTypes.bool,
  onTransferMoney: PropTypes.func.isRequired,
  onPayoutSchedule: PropTypes.func.isRequired,
  onBankAccount: PropTypes.func.isRequired,
  onEditRates: PropTypes.func.isRequired,
  onViewConfig: PropTypes.func.isRequired,
  onUpdateBusinessLines: PropTypes.func.isRequired,
};

export default AdyenSettingsActions;
